import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listDAppConfigs, listMappings, DAppConfig, MappingRecord } from '../services/trustline';
import { byModifiedThenName, formatDate, latest, NO_DATE_HINT } from '../utils/dates';
import { engineTypeBadgeClass } from '../utils/engineBadges';
import { getNetworkForDApp } from '../config/networks';
import ActionBanner from '../components/ActionBanner';
import DashIconButton from '../components/DashIconButton';
import ContractAddress from '../components/ContractAddress';
import '../components/layout/DashboardLayout.css';

const ChainCell: React.FC<{ chainId: string; type: string }> = ({ chainId, type }) => {
  const network = getNetworkForDApp(chainId, type);
  if (!network) {
    return <span className="dash-mono">{chainId}</span>;
  }
  return (
    <span className="dash-chain">
      {network.name} <span className="dash-mono dash-chain-id">{chainId}</span>
    </span>
  );
};

/** Filter key for a chain: the engine type is part of it because chain IDs are reused across engines. */
const chainKey = (d: DAppConfig): string => `${d.type}:${d.chainId}`;

const chainLabel = (d: DAppConfig): string => {
  const network = getNetworkForDApp(d.chainId, d.type);
  return network ? `${network.name} (${d.chainId})` : `${d.type} chain ${d.chainId}`;
};

const DAppsListPage: React.FC = () => {
  const [dApps, setDApps] = useState<DAppConfig[]>([]);
  const [mappings, setMappings] = useState<MappingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [chainFilter, setChainFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [res, mappingsRes] = await Promise.all([listDAppConfigs(), listMappings()]);
      if (cancelled) return;
      if (res.success && res.data?.dAppConfigs) {
        setDApps(res.data.dAppConfigs);
      } else {
        setError(res.error || 'Failed to load dApps');
      }
      setMappings(mappingsRes.success && mappingsRes.data?.mappings ? mappingsRes.data.mappings : []);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const chainOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    dApps.forEach((d) => byKey.set(chainKey(d), chainLabel(d)));
    return Array.from(byKey, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [dApps]);

  const typeOptions = useMemo(
    () => Array.from(new Set(dApps.map((d) => d.type))).sort((a, b) => a.localeCompare(b)),
    [dApps]
  );

  /** A dApp's last change: its own date, or its latest mapping's (when the API provides them). */
  const modifiedAt = (d: DAppConfig) =>
    latest(d.updatedAt, d.createdAt, ...mappings.filter((m) => m.dAppConfigId === d.id).map((m) => m.updatedAt ?? m.createdAt));

  const filteredDApps = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return dApps.filter(
      (d) =>
        (!query ||
          d.contractAddress.toLowerCase().includes(query) ||
          (d.name ?? '').toLowerCase().includes(query)) &&
        (!chainFilter || chainKey(d) === chainFilter) &&
        (!typeFilter || d.type === typeFilter)
    ).sort(byModifiedThenName(modifiedAt, (d) => d.name ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dApps, mappings, searchQuery, chainFilter, typeFilter]);

  const filtersActive = Boolean(searchQuery.trim() || chainFilter || typeFilter);

  const clearFilters = () => {
    setSearchQuery('');
    setChainFilter('');
    setTypeFilter('');
  };

  if (loading) {
    return <div className="loading">Loading dApps…</div>;
  }

  return (
    <div>
      <div className="dash-page-header">
        <h2>My dApps</h2>
        <p>All onboarded applications for this client.</p>
      </div>

      {error && <div className="dash-error">{error}</div>}

      <ActionBanner
        title="Protect a new smart contract"
        text="Onboard a dApp: register its contract, then choose which methods Trustline checks and with which policy."
        label="Onboard a new dApp"
        to="/dapps/new"
      />

      <div className="dash-card">
        {dApps.length > 0 && (
          <div className="dash-filters">
            <input
              type="search"
              className="dash-filter-search"
              placeholder="Search by name or contract address"
              aria-label="Search by name or contract address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
            <select
              className="dash-filter-select"
              aria-label="Filter by chain"
              value={chainFilter}
              onChange={(e) => setChainFilter(e.target.value)}
            >
              <option value="">All chains</option>
              {chainOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              className="dash-filter-select"
              aria-label="Filter by type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {filtersActive && (
              <button type="button" className="dash-btn dash-btn-secondary" onClick={clearFilters}>
                Clear
              </button>
            )}
            <span className="dash-filter-count">
              {filtersActive
                ? `${filteredDApps.length} of ${dApps.length} dApps`
                : `${dApps.length} ${dApps.length === 1 ? 'dApp' : 'dApps'}`}
            </span>
          </div>
        )}
        {dApps.length === 0 ? (
          <p className="dash-empty">
            No dApps yet.{' '}
            <Link to="/dapps/new" className="dash-link">
              Onboard your first dApp
            </Link>
          </p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Last modified</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Chain</th>
                  <th>Contract</th>
                  <th>Date created</th>
                </tr>
              </thead>
              <tbody>
                {filteredDApps.length === 0 && (
                  <tr>
                    <td colSpan={7} className="dash-empty">
                      No dApp matches these filters.
                    </td>
                  </tr>
                )}
                {filteredDApps.map((d) => (
                  <tr key={d.id}>
                    <td className="dash-actions-cell">
                      <DashIconButton
                        action="manage"
                        title={`Manage ${d.name}`}
                        to={`/dapps/${encodeURIComponent(d.chainId)}/${encodeURIComponent(d.contractAddress)}`}
                      />
                    </td>
                    <td title={modifiedAt(d) ? undefined : NO_DATE_HINT}>{formatDate(modifiedAt(d))}</td>
                    <td>{d.name}</td>
                    <td>
                      <span className={engineTypeBadgeClass(d.type)}>{d.type}</span>
                    </td>
                    <td>
                      <ChainCell chainId={d.chainId} type={d.type} />
                    </td>
                    <td>
                      <ContractAddress address={d.contractAddress} />
                    </td>
                    <td title={d.createdAt ? undefined : NO_DATE_HINT}>{formatDate(d.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DAppsListPage;
